import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { resolvePermKey } from "@/lib/roles";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem("kippu_user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);

  const loadPerms = useCallback(async () => {
    try {
      const { data } = await api.get("/permissions/matrix");
      setPerms(data || {});
    } catch {
      setPerms({});
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("kippu_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("kippu_user", JSON.stringify(r.data));
        return loadPerms();
      })
      .catch(() => {
        localStorage.removeItem("kippu_token");
        localStorage.removeItem("kippu_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, [loadPerms]);

  // Multi-tenant: role + permissions are per-client — refresh when switching.
  useEffect(() => {
    const onSwitch = () => {
      api.get("/auth/me").then((r) => {
        setUser(r.data);
        localStorage.setItem("kippu_user", JSON.stringify(r.data));
      }).catch(() => {});
      loadPerms();
    };
    window.addEventListener("kippu-client-switched", onSwitch);
    return () => window.removeEventListener("kippu-client-switched", onSwitch);
  }, [loadPerms]);

  const requestOtp = async (email) => {
    const { data } = await api.post("/auth/request-otp", { email });
    return data;
  };

  const verifyOtp = async (email, code) => {
    const { data } = await api.post("/auth/verify-otp", { email, code });
    localStorage.setItem("kippu_token", data.access_token);
    localStorage.setItem("kippu_user", JSON.stringify(data.user));
    // Multi-tenant: seed the active client from the server-provided default
    // so the very first authenticated request already carries X-Client-Id.
    if (data.user?.active_client_id) {
      localStorage.setItem("kippu_client_id", data.user.active_client_id);
    }
    setUser(data.user);
    await loadPerms();
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("kippu_token");
    localStorage.removeItem("kippu_user");
    localStorage.removeItem("kippu_client_id");
    setUser(null);
    setPerms({});
  };

  const hasPerm = useCallback(
    (action) => {
      if (!user) return false;
      if (user.is_super_admin) return true;
      const key = resolvePermKey(action);
      // Per-user effective permissions (role defaults + overrides) are the
      // source of truth for gating the current user's UI.
      if (Array.isArray(user.effective_permissions)) {
        return user.effective_permissions.includes(key);
      }
      const role = user.role || "l1_agent";
      return !!perms[role]?.[key];
    },
    [user, perms]
  );

  // True if the user holds ANY of the supplied permission keys.
  const hasAnyPerm = useCallback(
    (actions = []) => actions.some((a) => hasPerm(a)),
    [hasPerm]
  );

  return (
    <AuthCtx.Provider value={{ user, loading, requestOtp, verifyOtp, logout, hasPerm, hasAnyPerm, perms, reloadPerms: loadPerms }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
